<template>
  <Grid
    class="submissions-grid"
    :pageable="pageable"
    :data-items="submissionJobs"
    :skip="skip"
    :take="take"
    :page-size="take"
    :total="total"
    :columns="columns"
    :loader="loading"
    :filter="filter"
    :column-menu="!loading"
    :sort="sort"
    @columnreorder="onColumnReorder"
    @filterchange="filterChangeHandler"
    @pagechange="pageChangeHandler"
    @sortchange="sortChangeHandler"
    data-cy="submissions-grid"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="columns"
        :getItems="getPropValues"
        @sortchange="(e) => props.onSortchange(e)"
        @filterchange="(e) => props.onFilterchange(e)"
        @closemenu="(e: any) => props.onClosemenu(e)"
        @contentfocus="(e: any) => props.onContentfocus(e)"
        @columnssubmit="onColumnsSubmit"
      />
    </template>

    <template v-slot:UrgentCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <img
          class="urgent-icon"
          :src="dataItem.IsUrgent ? urgentIcon : ''"
          :alt="dataItem.IsUrgent ? 'Urgent' : ''"
        />
      </td>
    </template>

    <template v-slot:DetailsCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <a
          :href="`${Pages.WorkflowDetailIngredients}?JobID=${dataItem.JobId}&HistoryMode=HideSome&IARSubmissionsPage=True&activeTab=${tabChoice}`"
          type="image"
        >
          <img
            class="fa fa-external-link details-icon"
            alt="Detail Icon"
            :src="detailIconUrl"
          />
        </a>
      </td>
    </template>
    <template v-slot:CheckOutCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <a
          :href="`${Pages.WorkflowDetailIngredients}?JobID=${dataItem.JobId}&CheckOut=true&IARSubmissionsPage=True&activeTab=${tabChoice}`"
        >
          Check Out
        </a>
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import {
  Grid,
  type GridColumnReorderEvent,
  type GridColumnProps,
  type GridFilterChangeEvent,
  type GridPageChangeEvent,
  type GridPagerSettings,
} from "@progress/kendo-vue-grid";
import { IarTab } from "@/Domain/IarTab";
import { ref, watchEffect, watch } from "vue";
import urgentIcon from "@/assets/img/Urgent1Red.png";
import detailIconUrl from "@/assets/img/DetailIcon.png";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";
import {
  getFilterOperatorsForDate,
  getFilterOperatorsForNumber,
} from "@/shared/helpers/filtersHelper";
import type { SubmissionsTable } from "@/Domain/SubmissionsTable";
import type { ItemSortChangedOptions } from "@/Domain/ItemChangedOptions";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import type {
  CompositeFilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import { Pages } from "@/pages/pages";

const props = defineProps<{
  submissionJobs: SubmissionsTable[];
  loading: boolean;
  skip: number;
  pageSize: number;
  total: number;
  filter: CompositeFilterDescriptor;
  tabChoice: string;
  sort: SortDescriptor[];
  getPropValues: (
    column: GridColumnPropsExtended
  ) => Promise<SubmissionsTable[]>;
}>();

const emit = defineEmits<{
  (
    e: "submissionsChanged",
    skip: number,
    take: number,
    filterEvent: GridFilterChangeEvent | null
  ): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
  (e: "columnreorder", event: GridColumnReorderEvent): void;
  (e: "columnssubmit", columnsProps: GridColumnProps[]): void;
}>();

const skip = ref<number>(props.skip);
const take = ref<number>(props.pageSize);

const getColumns = function () {
  const cols = [] as GridColumnPropsExtended[];

  cols.push(
    {
      cell: "UrgentCell",
      headerClassName: "hide-menu",
      sortable: false,
      filterable: false,
      width: "40px",
    },
    {
      cell: "DetailsCell",
      headerClassName: "hide-menu",
      sortable: false,
      filterable: false,
      width: "40px",
      hideInColumnMenu: true,
    },
    {
      title: "Job ID",
      field: "JobId",
      columnMenu: "columnMenu",
      width: "60px",
      hideInColumnMenu: true,
      allowedFilterOperators: getFilterOperatorsForNumber(),
    },
    {
      title: "Company Name",
      field: "CompanyName",
      columnMenu: "columnMenu",
    },
    {
      title: "Company Id",
      field: "CompanyID",
      columnMenu: "columnMenu",
      width: "100px",
      allowedFilterOperators: getFilterOperatorsForNumber(),
    },
    {
      title: "RC",
      field: "RC",
      columnMenu: "columnMenu",
    }
  );

  if (props.tabChoice === IarTab.AllJobs.toString()) {
    cols.push({
      title: "User Login",
      field: "UserLogin",
      columnMenu: "columnMenu",
    });
  }

  cols.push({
    title: "Date Submitted",
    field: "DateProcessedForSort",
    columnMenu: "columnMenu",
    type: "date",
    format: "{0:MM/dd/yyyy h:mm a}",
    allowedFilterOperators: getFilterOperatorsForDate(),
  });

  if (props.tabChoice === IarTab.AllJobs.toString()) {
    cols.push({
      title: "Email",
      field: "UserEmail",
      columnMenu: "columnMenu",
    });
  }

  cols.push(
    {
      title: "Line Items",
      field: "LineItems",
      columnMenu: "columnMenu",
      width: "80px",
      allowedFilterOperators: getFilterOperatorsForNumber(),
    },
    {
      title: "Status",
      field: "Status",
      columnMenu: "columnMenu",
      width: "80px",
    }
  );

  if (props.tabChoice === IarTab.OpenJobs.toString()) {
    cols.push({
      cell: "CheckOutCell",
      title: "Checked Out",
      field: "CheckedOutBy",
      editable: false,
      sortable: false,
      filterable: false,
      width: "100px",
      columnMenu: "columnMenu",
    });
  }

  if (props.tabChoice === IarTab.AllJobs.toString()) {
    cols.push({
      title: "Checked Out By",
      field: "CheckedOutBy",
      columnMenu: "columnMenu",
    });
  }

  if (
    props.tabChoice === IarTab.MyOpenJobs.toString() ||
    props.tabChoice === IarTab.AllJobs.toString()
  ) {
    cols.push(
      {
        title: "Checked Out Date",
        field: "CheckedOutDateForSort",
        columnMenu: "columnMenu",
        type: "date",
        format: "{0:MM/dd/yyyy}",
        allowedFilterOperators: getFilterOperatorsForDate(),
      },
      {
        title: "Assigned To",
        field: "AssignedTo",
        columnMenu: "columnMenu",
      },
      {
        title: "Wf Updated",
        field: "WfLastUpdateDateForSort",
        columnMenu: "columnMenu",
        type: "date",
        format: "{0:MM/dd/yyyy h:mm:ss a}",
        allowedFilterOperators: getFilterOperatorsForDate(),
      }
    );
  }

  cols.push(
    {
      title: "Reviewed By",
      field: "ApprovedBy",
      columnMenu: "columnMenu",
    },
    {
      title: "Reviewed Date",
      field: "ApprovalDateForSort",
      columnMenu: "columnMenu",
      type: "date",
      format: "{0:MM/dd/yyyy h:mm:ss a}",
      allowedFilterOperators: getFilterOperatorsForDate(),
    }
  );
  return cols;
};

const columns = ref<GridColumnPropsExtended[]>(getColumns());

let pageSizes = [10, 20, 25, 50, 100];
if (!pageSizes.includes(take.value)) pageSizes.push(take.value);
pageSizes = pageSizes.sort((a, b) => a - b);
const pageable = ref<GridPagerSettings>({
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: pageSizes,
  previousNext: true,
});

const filterChangeHandler = function (e: GridFilterChangeEvent) {
  skip.value = 0;
  emit("submissionsChanged", 0, take.value, e);
};

const sortChangeHandler = function (e: any) {
  const descriptors = e.sort as SortDescriptor[];
  if (!descriptors) return;
  skip.value = 0;
  emit("sortChanged", {
    field: descriptors[0].field,
    ascending: descriptors[0].dir === "asc",
  });
};

const pageChangeHandler = function (event: GridPageChangeEvent) {
  skip.value = event.page.skip;
  take.value = event.page.take;
  emit("submissionsChanged", event.page.skip, event.page.take, null);
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  if (columnsProps) columns.value = columnsProps;
  console.debug("InvoicesGrid.vue, onColumnSubmit", columnsProps);
  emit("columnssubmit", columnsProps);
};

const onColumnReorder = function (e: GridColumnReorderEvent) {
  emit("columnreorder", e);
};

watchEffect(() => {
  skip.value = props.skip;
});

watchEffect(() => {
  take.value = props.pageSize;
});

watch(
  () => props.tabChoice,
  () => {
    columns.value = getColumns();
  }
);
</script>

<style scoped lang="scss">
.submissions-grid {
  height: 600px;
}

[src=""] {
  visibility: hidden;
  width: 0;
}

.urgent-icon {
  width: 20px;
  height: 20px;
}

.k-grid {
  a.is-download-link {
    svg {
      height: 12px;
    }

    &:before {
      content: "";
    }
  }

  .k-pager {
    .k-input-inner {
      padding: 0.375rem 0.375rem;
    }
  }
}
</style>
